<script setup>
import { ref, reactive, onMounted, computed } from 'vue';
import { message } from 'ant-design-vue';
import { showErrorMessage } from '@/utils/messageHelper';
import { useRouter } from 'vue-router';
import { useAdminInfo } from '@/stores/useAdminInfo';
import DataTable from '@/components/common/DataTable.vue';
import { getLevelLogs } from '@/api/levels';
import formatDateTime from '@/utils/formatDateTime';
import dayjs from 'dayjs';
import { searchUsers } from '@/api/users';

const router = useRouter();
const adminStore = useAdminInfo();

// 權限檢查
const canView = computed(() => adminStore.hasPermissionCode('120200')); // 等級紀錄

// 查看會員詳情
const handleViewMember = (userId) => {
    if (!userId) return;
    if (!adminStore.hasPermissionCode('100110')) {
        // 會員詳情
        message.warning('您沒有查看會員詳情的權限');
        return;
    }
    router.push(`/members/${userId}`);
};

// 搜尋表單狀態
const searchForm = reactive({
    user_id: undefined,
    upgraded_range: []
});

// 會員搜尋相關
const userOptions = ref([]);
const userLoading = ref(false);
let userSearchTimer = null;
const USER_SEARCH_DEBOUNCE_MS = 250;

const fetchUserOptions = async (keyword) => {
    if (!keyword || keyword.trim().length < 2) {
        userOptions.value = [];
        return;
    }
    try {
        userLoading.value = true;
        const res = await searchUsers({ name: keyword.trim(), limit: 10 });
        if (res?.result) {
            const arr = Array.isArray(res.data)
                ? res.data
                : res.data?.data || [];
            userOptions.value = arr.map((u) => ({
                label: `${u.name || u.user_name || `#${u.id}`}${u.mail || u.email ? ` (${u.mail || u.email})` : ''}`,
                value: u.id
            }));
        }
    } catch {
    } finally {
        userLoading.value = false;
    }
};

const onUserSearch = (val) => {
    clearTimeout(userSearchTimer);
    userSearchTimer = setTimeout(
        () => fetchUserOptions(val),
        USER_SEARCH_DEBOUNCE_MS
    );
};

// 表格狀態
const tableData = ref([]);
const loading = ref(false);
const pagination = reactive({ total: 0, current: 1, pageSize: 20 });
const lastQueryRef = ref({});

// 欄位定義
const columns = [
    {
        title: '會員',
        key: 'user',
        width: 200,
        slots: { customRender: 'user' }
    },
    {
        title: '等級變動',
        key: 'level_change',
        width: 150,
        slots: { customRender: 'level_change' }
    },
    {
        title: '觸發來源',
        dataIndex: 'trigger_source',
        key: 'trigger_source',
        width: 120
    },
    {
        title: '身份',
        key: 'role',
        width: 100,
        slots: { customRender: 'role' }
    },
    {
        title: '聘階',
        key: 'rank',
        width: 120,
        slots: { customRender: 'rank' }
    },
    {
        title: '狀態',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        slots: { customRender: 'status' }
    },
    {
        title: '原因',
        dataIndex: 'reason',
        key: 'reason',
        width: 150
    },
    {
        title: '操作者',
        key: 'reviewer_name',
        width: 120,
        slots: { customRender: 'reviewer_name' }
    },
    {
        title: '升級時間',
        dataIndex: 'upgraded_at',
        key: 'upgraded_at',
        width: 180,
        slots: { customRender: 'upgraded_at' }
    }
];

// 查詢函式（集中處理 API 錯誤）
const fetchLogs = async (extraParams = {}) => {
    try {
        loading.value = true;
        const params = {
            page: pagination.current,
            per_page: pagination.pageSize,
            ...lastQueryRef.value,
            ...extraParams
        };
        const res = await getLevelLogs(params);
        if (res?.result || res?.success) {
            // 兼容不同返回格式
            const list = res.data?.data || res.data?.list || [];
            const paginationData = res.data?.pagination || {};
            tableData.value = list;
            pagination.total = paginationData.total || 0;
            pagination.current = paginationData.current_page || 1;
            return res;
        } else {
            showErrorMessage(res?.message || '取得等級紀錄失敗');
            tableData.value = [];
            pagination.total = 0;
            return res;
        }
    } catch (err) {
        console.error('GET /api/admin/logs/level 失敗', err);
        tableData.value = [];
        pagination.total = 0;
        return null;
    } finally {
        loading.value = false;
    }
};

// 轉換搜尋表單為 API 參數
const normalizeSearchParams = (params = {}) => {
    const p = { ...params };
    if (Array.isArray(p.upgraded_range) && p.upgraded_range.length === 2) {
        const [start, end] = p.upgraded_range;
        p.upgraded_from = start ? dayjs(start).format('YYYY-MM-DD') : undefined;
        p.upgraded_to = end ? dayjs(end).format('YYYY-MM-DD') : undefined;
    }
    delete p.upgraded_range;
    return Object.fromEntries(
        Object.entries(p).filter(
            ([, v]) => v !== '' && v !== undefined && v !== null
        )
    );
};

const handleSearch = async () => {
    pagination.current = 1;
    lastQueryRef.value = normalizeSearchParams(searchForm);
    await fetchLogs();
};

const handleReset = async () => {
    searchForm.user_id = undefined;
    searchForm.upgraded_range = [];
    pagination.current = 1;
    lastQueryRef.value = {};
    await fetchLogs();
};

// 現在定義回調函數
const onUserChange = (val) => {
    handleSearch();
};

const onUserClear = () => {
    searchForm.user_id = undefined;
    handleSearch();
};

const onDateRangeChange = (val) => {
    searchForm.upgraded_range = val;
    handleSearch();
};

// 更新 searchItems 的回調 - 在 onMounted 中執行，確保 SearchCard 已初始化
onMounted(async () => {
    await fetchLogs();
});

// 表格換頁/排序
const handleTableChange = async ({ pagination: p }) => {
    pagination.current = p.current;
    pagination.pageSize = p.pageSize;
    await fetchLogs();
};

// 導出給模板使用
const tableColumns = computed(() => columns);
</script>

<template>
    <div class="page levels-page">
        <div class="page-header">
            <h1>等級紀錄</h1>
            <p>查看與追蹤會員的等級異動紀錄</p>
        </div>

        <div class="content vaporwave-theme">
            <!-- 工具欄 -->
            <div class="toolbar">
                <div class="search-box filters-wrap">
                    <a-select
                        v-model:value="searchForm.user_id"
                        show-search
                        :filter-option="false"
                        :options="userOptions"
                        :loading="userLoading"
                        allow-clear
                        placeholder="搜尋會員"
                        class="status-select min-w-[300px]"
                        size="middle"
                        @search="onUserSearch"
                        @change="onUserChange"
                        @clear="onUserClear"
                    />
                    <a-range-picker
                        v-model:value="searchForm.upgraded_range"
                        :placeholder="['開始日期', '結束日期']"
                        class="status-select"
                        size="middle"
                        @change="onDateRangeChange"
                    />
                </div>
                <div class="filter-actions">
                    <a-button
                        size="middle"
                        class="unified-search-btn"
                        @click="handleSearch"
                    >
                        查詢
                    </a-button>
                    <a-button
                        size="middle"
                        class="unified-reset-btn"
                        @click="handleReset"
                    >
                        重置
                    </a-button>
                </div>
            </div>

            <!-- 等級紀錄表格 -->
            <DataTable
                title=""
                :columns="tableColumns"
                :data-source="tableData"
                :pagination="pagination"
                :show-pagination="true"
                :scroll="{ x: 1200 }"
                :loading="loading"
                @change="handleTableChange"
                class="levels-table left-align-table"
                :no-card="true"
            >
                <template #user="{ record }">
                    <a
                        v-if="record.user_id"
                        class="user-link"
                        @click.stop="handleViewMember(record.user_id)"
                    >
                        {{ record.user_name || '-' }}
                    </a>
                    <span v-else class="user-name">
                        {{ record.user_name || '-' }}
                    </span>
                </template>
                <template #level_change="{ record }">
                    <span class="level-change">
                        Lv.{{ record.from_level }} → Lv.{{ record.to_level }}
                    </span>
                </template>
                <template #role="{ record }">
                    <span v-if="record.role_name">
                        {{ record.role_name }}
                    </span>
                    <span v-else>-</span>
                </template>
                <template #rank="{ record }">
                    <span v-if="record.rank_name">
                        {{ record.rank_name }}
                    </span>
                    <span v-else>-</span>
                </template>
                <template #reviewer_name="{ record }">
                    {{ record.reviewer_name || '-' }}
                </template>
                <template #status="{ text }">
                    <a-tag
                        :color="
                            text === '成功'
                                ? 'green'
                                : text === '失敗'
                                  ? 'red'
                                  : 'default'
                        "
                    >
                        {{ text }}
                    </a-tag>
                </template>
                <template #upgraded_at="{ text }">
                    {{ text ? formatDateTime(text) : '-' }}
                </template>
            </DataTable>
        </div>
    </div>
</template>

<style scoped>
.levels-page {
    padding: 24px;
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    min-height: 100vh;
}

.page-header {
    margin-bottom: 24px;
}

.page-header h1 {
    font-size: 24px;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: #2a2a3a;
    font-family: 'Courier New', monospace;
}

.page-header p {
    margin: 0;
    color: #6a6a7a;
    font-size: 14px;
    font-family: 'Courier New', monospace;
}

/* 工具欄樣式 - 對齊會員積分頁 */
.toolbar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    position: relative;
    z-index: 1;
    flex-wrap: wrap;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(200, 200, 210, 0.4);
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.1);
}

.search-box {
    display: flex;
    align-items: center;
    gap: 16px;
}

/* 篩選操作按鈕容器 */
.filter-actions {
    display: flex;
    gap: 8px;
}

/* 統一篩選欄位樣式 */
.toolbar :deep(.status-select),
.toolbar :deep(.status-select .ant-select-selector),
.toolbar :deep(.status-select.ant-picker) {
    height: 32px;
    font-size: 13px;
    border-radius: 8px;
    border: 1.5px solid rgba(200, 200, 210, 0.4);
    background: rgba(255, 255, 255, 0.95);
    color: #2a2a3a;
    transition: all 0.3s ease;
}

.toolbar :deep(.status-select:hover),
.toolbar :deep(.status-select.ant-select:hover .ant-select-selector),
.toolbar :deep(.status-select.ant-picker:hover) {
    border-color: rgba(42, 74, 122, 0.6);
    box-shadow: 0 2px 8px rgba(42, 74, 122, 0.2);
}

.toolbar :deep(.status-select.ant-select-focused .ant-select-selector),
.toolbar :deep(.status-select.ant-picker-focused) {
    border-color: rgba(42, 74, 122, 0.8);
    box-shadow: 0 0 0 2px rgba(42, 74, 122, 0.2);
}

/* 確保查詢和重置按鈕樣式優先 */
.filter-actions .unified-search-btn,
.filter-actions .unified-search-btn.ant-btn,
.filter-actions .unified-search-btn.ant-btn-default,
.filter-actions :deep(.unified-search-btn),
.filter-actions :deep(.unified-search-btn.ant-btn),
.filter-actions :deep(.unified-search-btn.ant-btn-default) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #7a9ba5 !important;
    color: #7a9ba5 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
    box-shadow: none !important;
}

.filter-actions .unified-search-btn:hover,
.filter-actions .unified-search-btn.ant-btn:hover,
.filter-actions .unified-search-btn.ant-btn-default:hover,
.filter-actions :deep(.unified-search-btn:hover),
.filter-actions :deep(.unified-search-btn.ant-btn:hover),
.filter-actions :deep(.unified-search-btn.ant-btn-default:hover) {
    background: rgba(122, 155, 165, 0.1) !important;
    border-color: #7a9ba5 !important;
    color: #7a9ba5 !important;
    box-shadow: 0 2px 8px rgba(122, 155, 165, 0.3) !important;
}

.filter-actions .unified-reset-btn,
.filter-actions .unified-reset-btn.ant-btn,
.filter-actions .unified-reset-btn.ant-btn-default,
.filter-actions :deep(.unified-reset-btn),
.filter-actions :deep(.unified-reset-btn.ant-btn),
.filter-actions :deep(.unified-reset-btn.ant-btn-default) {
    background: rgba(255, 255, 255, 0.95) !important;
    border: 1.5px solid #c4a5a5 !important;
    color: #c4a5a5 !important;
    font-family: 'Courier New', monospace !important;
    font-weight: 500 !important;
    border-radius: 8px !important;
    transition: all 0.3s ease !important;
    box-shadow: none !important;
}

.filter-actions .unified-reset-btn:hover,
.filter-actions .unified-reset-btn.ant-btn:hover,
.filter-actions .unified-reset-btn.ant-btn-default:hover,
.filter-actions :deep(.unified-reset-btn:hover),
.filter-actions :deep(.unified-reset-btn.ant-btn:hover),
.filter-actions :deep(.unified-reset-btn.ant-btn-default:hover) {
    background: rgba(196, 165, 165, 0.1) !important;
    border-color: #c4a5a5 !important;
    color: #c4a5a5 !important;
    box-shadow: 0 2px 8px rgba(196, 165, 165, 0.3) !important;
}

/* 以會員積分頁相同的卡片外觀包住內容 */
.content {
    background: linear-gradient(
        135deg,
        rgba(255, 255, 255, 0.95),
        rgba(248, 249, 250, 0.9)
    );
    border: 2px solid transparent;
    border-radius: 16px;
    padding: 32px;
    position: relative;
    box-shadow: 0 0 30px rgba(42, 74, 122, 0.3);
}

.content::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    padding: 2px;
    background: linear-gradient(45deg, #64748b, #2a4a7a, #3b6ea5);
    mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
    mask-composite: exclude;
    -webkit-mask-composite: xor;
}

::deep(.ant-card-body) {
    padding: 0 !important;
}

.user-name {
    color: #2a2a3a;
    font-weight: 600;
}

.user-link {
    color: #3b6ea5;
    font-weight: 600;
    cursor: pointer;
    text-decoration: none;
    transition: all 0.2s ease;
    font-family: 'Courier New', monospace;
}

.user-link:hover {
    color: #2a4a7a;
    text-decoration: underline;
    text-shadow: 0 0 8px rgba(42, 74, 122, 0.3);
}

.level-change {
    color: #2a2a3a;
    font-weight: 600;
    font-family: 'Courier New', monospace;
}
</style>
